import { useState } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  PiLink,
  PiLinkBreak,
  PiMagnifyingGlass,
  PiPlus,
  PiWarning,
} from 'react-icons/pi';
import { Link } from 'wouter';
import { semanticJurisdiction } from '@/lib/helpers';
import {
  TOffer,
  useAttachPayer,
  useDetachPayer,
  verificationInFlight,
  type VerificationInFlightBody,
} from '@/models/offers';
import { TPayer, usePayers } from '@/models/payers';
import { KycStateBadge } from '@/components/PayerKycBadge';
import PayerCreateForm from '@/components/PayerCreateForm';
import CancelVerificationModal from '@/components/CancelVerificationModal';
import { useConfirmation } from '@/hooks/useConfirmation';

type PayerCardData = {
  displayName: string;
  clientType: string;
  countryCode: string;
  kycExpiresAt: string | null;
};

function PayerCard({ payer }: { payer: PayerCardData }) {
  return (
    <Group gap="md" wrap="wrap">
      <Box>
        <Text size="xs" c="dimmed">
          Name
        </Text>
        <Text size="sm" fw={500}>
          {payer.displayName}
        </Text>
      </Box>
      <Box>
        <Text size="xs" c="dimmed">
          Type
        </Text>
        <Text size="sm">{payer.clientType}</Text>
      </Box>
      <Box>
        <Text size="xs" c="dimmed">
          Country
        </Text>
        <Text size="sm">{semanticJurisdiction(payer.countryCode)}</Text>
      </Box>
      <Box>
        <Text size="xs" c="dimmed">
          KYC
        </Text>
        <KycStateBadge kycExpiresAt={payer.kycExpiresAt} size="sm" />
      </Box>
    </Group>
  );
}

export function AttachPayerModal({
  opened,
  onClose,
  offer,
}: {
  opened: boolean;
  onClose: () => void;
  offer: TOffer;
}) {
  const [view, setView] = useState<'list' | 'create'>('list');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data: payers = [], isLoading } = usePayers(
    offer.accountId ?? undefined,
  );
  const attachPayer = useAttachPayer();
  const [conflict, setConflict] = useState<{
    body: VerificationInFlightBody;
    payerId: number;
  } | null>(null);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? payers.filter((p) => p.displayName.toLowerCase().includes(term))
    : payers;
  const selected = payers.find((p) => p.id === selectedId) ?? null;

  const handleClose = () => {
    setSearch('');
    setSelectedId(null);
    setView('list');
    onClose();
  };

  const attach = async (payerId: number, reason?: string) => {
    try {
      await attachPayer.mutateAsync({
        offerId: offer.id,
        payerId,
        ...(reason ? { cancelVerification: true as const, reason } : {}),
      });
      setConflict(null);
      handleClose();
    } catch (e) {
      // A verification in progress is not a failure to report and forget: it is
      // a decision to put to the user, who may be allowed to override it.
      const inFlight = verificationInFlight(e);
      if (inFlight) {
        setConflict({ body: inFlight, payerId });
        return;
      }
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to attach paying party: ${e}`,
      });
    }
  };

  const handleCreated = (payer: TPayer) => {
    attach(payer.id);
  };

  return (
    <>
      <CancelVerificationModal
        conflict={conflict?.body ?? null}
        busy={attachPayer.isPending}
        onConfirm={(reason) => conflict && attach(conflict.payerId, reason)}
        onClose={() => setConflict(null)}
      />
      <Modal
        opened={opened}
        onClose={handleClose}
        title={
          view === 'create'
            ? 'Create New Payer'
            : `Payers of ${offer.accountName ?? 'account'}`
        }
        size="xl"
      >
        {view === 'create' ? (
          <PayerCreateForm
            accountId={offer.accountId}
            onCreated={handleCreated}
            onCancel={() => setView('list')}
          />
        ) : (
          <Stack gap="md">
            <TextInput
              placeholder="Search payer by name..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<PiMagnifyingGlass size={14} />}
            />

            {isLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : filtered.length === 0 ? (
              <Paper withBorder p="sm">
                <Text fz="sm" c="dimmed" ta="center" py="xs">
                  {payers.length === 0
                    ? 'No payers in this account yet.'
                    : 'No payers match your search.'}
                </Text>
              </Paper>
            ) : (
              <ScrollArea.Autosize mah={320}>
                <Table highlightOnHover verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Country</Table.Th>
                      <Table.Th>Invoice Email</Table.Th>
                      <Table.Th>KYC</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filtered.map((p) => (
                      <Table.Tr
                        key={p.id}
                        onClick={() => setSelectedId(p.id)}
                        style={{ cursor: 'pointer' }}
                        bg={
                          p.id === selectedId
                            ? 'var(--mantine-color-blue-light)'
                            : undefined
                        }
                      >
                        <Table.Td fw={500}>{p.displayName}</Table.Td>
                        <Table.Td>{p.clientType}</Table.Td>
                        <Table.Td>
                          {semanticJurisdiction(p.countryCode)}
                        </Table.Td>
                        <Table.Td>
                          {p.verifiedEmail ? (
                            <Text fz="sm">{p.verifiedEmail}</Text>
                          ) : (
                            <Text fz="xs" c="orange.7">
                              None — cannot be invoiced
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td style={{ whiteSpace: 'nowrap' }}>
                          <KycStateBadge
                            kycExpiresAt={p.kycExpiresAt}
                            size="xs"
                          />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea.Autosize>
            )}

            <Group justify="space-between">
              <Button
                variant="light"
                leftSection={<PiPlus size={14} />}
                onClick={() => setView('create')}
              >
                Create New Payer
              </Button>
              <Button
                leftSection={<PiLink size={14} />}
                onClick={() => selected && attach(selected.id)}
                loading={attachPayer.isPending}
                disabled={!selected}
              >
                Attach Payer
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

export default function PayerSection({ offer }: { offer: TOffer }) {
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const detachPayer = useDetachPayer();
  const [detachConflict, setDetachConflict] =
    useState<VerificationInFlightBody | null>(null);
  const { confirm, ConfirmationDialog } = useConfirmation();
  const isAccepted = offer.accepted;

  const detach = async (reason?: string) => {
    try {
      await detachPayer.mutateAsync({
        offerId: offer.id,
        ...(reason ? { cancelVerification: true as const, reason } : {}),
      });
      setDetachConflict(null);
    } catch (e) {
      const inFlight = verificationInFlight(e);
      if (inFlight) {
        setDetachConflict(inFlight);
        return;
      }
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to detach paying party: ${e}`,
      });
    }
  };

  const handleDetach = async () => {
    const ok = await confirm({
      title: 'Detach Paying Party',
      message: `Detach "${offer.payer?.displayName}" from this offer?`,
      confirmButtonText: 'Detach',
    });
    if (!ok) return;
    await detach();
  };

  return (
    <>
      <ConfirmationDialog />
      <CancelVerificationModal
        conflict={detachConflict}
        busy={detachPayer.isPending}
        onConfirm={(reason) => detach(reason)}
        onClose={() => setDetachConflict(null)}
      />
      {!isAccepted && (
        <AttachPayerModal
          opened={modalOpened}
          onClose={closeModal}
          offer={offer}
        />
      )}
      <Paper withBorder p="md">
        <Stack gap="sm">
          <Text fw={600} fz="sm">
            Paying party
          </Text>
          {offer.payer ? (
            <>
              {offer.payer.deletedAt && (
                <Alert color="red" icon={<PiWarning size={16} />}>
                  <Text fz="sm">
                    The attached payer has been deleted and cannot be used any
                    more. The Offer cannot be accepted until the payer is
                    replaced or detached.
                  </Text>
                </Alert>
              )}
              <PayerCard payer={offer.payer} />
              {offer.prospectiveInvoiceRecipient?.blocked ? (
                <Alert color="orange" icon={<PiWarning size={16} />}>
                  <Text fz="sm">
                    {offer.prospectiveInvoiceRecipient.blockReason} Invoices for
                    this offer cannot be issued or sent.
                  </Text>
                </Alert>
              ) : offer.prospectiveInvoiceRecipient?.email ? (
                <Text fz="xs" c="dimmed">
                  Invoices are sent to{' '}
                  <Text span fz="xs" fw={500} c="dimmed">
                    {offer.prospectiveInvoiceRecipient.email}
                  </Text>
                </Text>
              ) : null}
              <Group justify="space-between">
                <Anchor
                  component={Link}
                  to={`~/payers/${offer.payer.id}`}
                  fz="xs"
                >
                  Open payer card
                </Anchor>
                {!isAccepted && (
                  <Group gap="xs">
                    {offer.payer.deletedAt && (
                      <Button
                        size="xs"
                        leftSection={<PiLink size={14} />}
                        onClick={openModal}
                      >
                        Replace Payer
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      leftSection={<PiLinkBreak size={14} />}
                      onClick={handleDetach}
                      loading={detachPayer.isPending}
                    >
                      Detach
                    </Button>
                  </Group>
                )}
              </Group>
            </>
          ) : (
            <>
              <Text fz="sm" c="dimmed">
                Will be collected once Offer is accepted
              </Text>
              {!isAccepted && (
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    leftSection={<PiLink size={14} />}
                    onClick={openModal}
                  >
                    Attach Payer
                  </Button>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </>
  );
}
